/**
 * @file PlanEditSheet.tsx
 * @brief "Edit plan" side sheet on Super Admin → Plans & seats: every field
 *        of one plan, both currencies and both billing periods
 *
 * @details
 * DES 22-10-2026 (W8). The sheet edits the page's form, the same values the
 * card rows edit in place; nothing is saved here. Done closes it and the
 * changes wait in the bar at the bottom of the page ("3 changes · Review and
 * save"). No Cancel: the ✕ closes (DES 16-10-2026).
 *
 * B20 / W17 (24-10-2026): every new plan number is a field here (history
 * days, Notes minutes and fair use, interpretation minimums, Business maximum
 * seats), each with what its 0 / Unlimited means; Free gets the same "Free
 * minutes" and "Expires" controls as its card. A kind this build doesn't know
 * shows read-only.
 *
 * Related: SubscriptionPlansCard.tsx, PlanSettingRow.tsx, ../launchPlans.ts
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import { cn } from "@/shared/theme/cn";
import {
  CURRENCY_SYMBOL,
  FREE_MODE_NUMBER_KEYS,
  FREE_RESET_MODE_KEY,
  HOURS_KEYS,
  PLAN_CURRENCIES,
  displayLabel,
  fieldHint,
  planRowGroup,
  readable,
  type PlanSetting,
} from "../launchPlans";
import { fieldError, type PlanForm } from "../planForm";
import { FreeModeControls } from "./FreeModeControls";
import { PlanSwitchRow } from "./PlanSettingRow";

/** Same unit, GBP beside USD: order by label, then currency. */
function byLabelThenCurrency(list: PlanSetting[]): PlanSetting[] {
  const labels = [...new Set(list.map((s) => s.label))];
  return labels.flatMap((l) =>
    PLAN_CURRENCIES.flatMap((c) => list.filter((s) => s.label === l && s.currency === c)),
  );
}

export function PlanEditSheet({
  open,
  onOpenChange,
  title,
  settings,
  form,
  switchHints,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  settings: PlanSetting[];
  form: PlanForm;
  switchHints: Record<string, string>;
}) {
  // B20: the Free numbers are "a month" or "in total" by the mode in the form.
  const once = form.value(FREE_RESET_MODE_KEY) === "one_time";
  const label = (s: PlanSetting) =>
    s.kind === "minor" && s.currency
      ? `${displayLabel(s)} (${CURRENCY_SYMBOL[s.currency]})`
      : (FREE_MODE_NUMBER_KEYS as readonly string[]).includes(s.key)
        ? `${displayLabel(s)}${once ? ", in total" : " a month"}`
        : displayLabel(s);

  const hint = (s: PlanSetting): string => {
    const before = form.baseline[s.key] ?? "";
    const what = fieldHint(s);
    const state =
      form.value(s.key) !== before ? `Was ${readable(s, before)}` : s.value === null ? "Default (not saved yet)" : "";
    return [what, state].filter(Boolean).join(" · ") || " ";
  };

  const field = (s: PlanSetting) => {
    const error = form.invalid.has(s.key) ? fieldError(s) : undefined;
    if (s.kind === "other") {
      return (
        <FormSheetField key={s.key}>
          <FormFieldGroup label={label(s)} hint="Not editable in this version of the page.">
            <Input value={form.value(s.key)} disabled />
          </FormFieldGroup>
        </FormSheetField>
      );
    }
    if (s.kind === "limit") {
      const unlimited = form.value(s.key).trim() === "-1";
      const before = form.baseline[s.key] ?? "";
      return (
        <FormSheetField key={s.key}>
          <FormFieldGroup label={label(s)} hint={hint(s)} error={error}>
            <Input
              value={unlimited ? "Unlimited" : form.value(s.key)}
              disabled={unlimited}
              inputMode="numeric"
              onChange={(e) => form.set(s.key, e.target.value)}
            />
          </FormFieldGroup>
          <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Switch
              checked={unlimited}
              onCheckedChange={(on) => form.set(s.key, on ? "-1" : before !== "-1" ? before : "")}
              aria-label={`${s.label}: Unlimited`}
            />
            {/NOTES_MINUTES$/.test(s.key) ? "Unlimited, fair use" : "Unlimited"}
          </label>
        </FormSheetField>
      );
    }
    return (
      <FormSheetField key={s.key}>
        <FormFieldGroup
          label={HOURS_KEYS.has(s.key) ? displayLabel(s) : label(s)}
          hint={hint(s)}
          error={error}
        >
          <Input
            value={form.value(s.key)}
            inputMode="decimal"
            onChange={(e) => form.set(s.key, e.target.value)}
          />
        </FormFieldGroup>
      </FormSheetField>
    );
  };

  const switches = settings.filter((s) => planRowGroup(s) === "switch");
  const prices = settings.filter((s) => planRowGroup(s) === "price");
  const included = settings.filter((s) => planRowGroup(s) === "included");
  const payg = byLabelThenCurrency(settings.filter((s) => planRowGroup(s) === "payg"));
  const monthFirst = [
    ...prices.filter((s) => s.interval === "month"),
    ...prices.filter((s) => s.interval === "year"),
  ];

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      <FormSheetContent side="right">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>Edit {title}</FormSheetTitle>
            <FormSheetDescription>
              Changes wait in the bar at the bottom of the page. Nothing is
              saved until you review and save there.
            </FormSheetDescription>
          </FormSheetHeader>

          <FormSheetBody>
            {switches.length ? (
              <div className="bg-background px-4 pt-3 md:px-5">
                {switches.map((s) => (
                  <PlanSwitchRow key={s.key} s={s} form={form} hint={switchHints[s.key]} />
                ))}
                <FreeModeControls setting={(k) => settings.find((x) => x.key === k)} form={form} />
              </div>
            ) : null}
            {monthFirst.length ? (
              <FormSheetSection title="Price" subtitle="United Kingdom pays in GBP, everyone else in USD.">
                <FormSheetFields>{monthFirst.map(field)}</FormSheetFields>
              </FormSheetSection>
            ) : null}
            {included.length ? (
              <FormSheetSection title="What's included">
                <FormSheetFields>{included.map(field)}</FormSheetFields>
              </FormSheetSection>
            ) : null}
            {payg.length ? (
              <FormSheetSection title="Pay as you go" subtitle="Per minute, after what's included.">
                <FormSheetFields>{payg.map(field)}</FormSheetFields>
              </FormSheetSection>
            ) : null}
          </FormSheetBody>

          <FormSheetFooter className="justify-end">
            <FormSheetFooterCell className="max-w-[50%]">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
              >
                Done
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}
